import api from "../../../utils/api";
import {
  EXPENSES_ENDPOINTS,
  type BalanceItem,
  type BalanceSheet,
  type FinancialMonth,
  type BudgetAdjustmentPlan,
  type BudgetLine,
  type CategoryDef,
  type CategoryInput,
  type CategoryRunRate,
  type CashflowSummary,
  type DocumentKindChoice,
  type Income,
  type IncomeExtraction,
  type IncomeInput,
  type IncomeMatch,
  type IncomePreview,
  type PayoutConfig,
  type PayoutReconciliation,
  type Expense,
  type ExpenseInput,
  type ExtractedCharge,
  type InboxResult,
  type MonthSummary,
  type LossSummary,
  type PayrollHoursMonth,
  type PayrollHoursPerson,
  type PayrollHoursResult,
  type Platform,
  type PlatformReport,
} from "@torre/expenses";

// ---------------------------------------------------------------------------
// Cliente API de Gastos y Presupuestos (#615). Las respuestas vienen como
// `{ meta, data }`: se devuelve `data`.
// ---------------------------------------------------------------------------

// La instancia de axios fija `Content-Type: application/json`; con eso axios
// convertiría el FormData en JSON y el archivo nunca llegaría.
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const data = <T>(res: any): T => res?.data?.data as T;

/** Mensaje legible de un error de la API (o `null`). */
export function apiErrorMessage(err: unknown): string | null {
  const r = (err as { response?: { data?: { meta?: { message?: string } } } })?.response;
  return r?.data?.meta?.message || null;
}

export type ExpenseRow = Expense & { readOnly?: boolean };

export interface AccessInfo {
  allowed: boolean;
  /** Dueño (#1027): rol owner + acceso a Finanzas. Ve la nómina pagada y abre estados de cuenta. */
  owner?: boolean;
  email: string | null;
  companyId: string | null;
  fixedEmails?: string[];
  savedEmails?: string[];
  /** La lista no se puede ampliar (CC-CONSOLE, #1007): solo los dueños. */
  accessLocked?: boolean;
  aiConfigured?: boolean;
  inbox?: InboxInfo;
}

export interface InboxInfo {
  configured: boolean;
  address: string | null;
  host?: string | null;
  mailbox?: string;
  companyId: string | null;
}

export interface InboxStatus extends InboxInfo {
  linkedToThisCompany: boolean;
  aiConfigured: boolean;
  state: { lastRunAt?: string; lastError?: string | null; lastProcessed?: number; lastFailed?: number } | null;
}

export interface InboxLogRow {
  id: string;
  messageId: string;
  from: string | null;
  subject: string | null;
  receivedAt: string | null;
  channel: "imap" | "manual";
  result: InboxResult;
  results: { result: InboxResult; expenseId?: string; detail?: string }[];
  expenseId: string | null;
  detail: string | null;
  attachments: { name: string; contentType: string }[];
  processedAt: string;
  retryable?: boolean;
}

export type PreviewCharge = ExtractedCharge & {
  index: number;
  duplicateOf: string | null;
  platformId: string | null;
};

export interface ImportRecord {
  id: string;
  status: "pendiente" | "confirmada";
  file: { path: string; name: string; contentType: string; size: number; encrypted?: boolean; statementId?: string | null };
  documentType: string;
  issuer: string | null;
  statementPeriod: { from: string | null; to: string | null } | null;
  statementTotal: number | null;
  currency: string | null;
  notes: string | null;
  charges: PreviewCharge[];
  totals: Record<string, number>;
  createdAt: string;
  createdExpenseIds: string[];
  chargeCount?: number;
}

export interface PayrollRate {
  rate: number;
  /** `gastos`: el más reciente pagado en la empresa; `manual`: fijado desde el resumen. */
  source: "banxico" | "frankfurter" | "gastos" | "manual";
  date: string | null;
}

export interface RosterPayroll {
  members: number;
  withoutSalary: number;
  monthlyByCurrency: Record<string, number>;
  rates: Record<string, PayrollRate>;
}

export interface SummaryResponse {
  from: string;
  to: string;
  months: MonthSummary[];
  total: number;
  payrollSkipped: number;
  /** Nómina del equipo estimada desde el padrón (sueldo de la quincena × 2). */
  roster?: RosterPayroll;
  budgets: BudgetLine[];
  platformCount: number;
  expenses: Pick<Expense, "id" | "date" | "period" | "category" | "amountBase" | "vendor" | "description" | "source">[];
}

export interface ListParams {
  from?: string;
  to?: string;
  category?: string;
  invoiceStatus?: string;
  status?: string;
  platformId?: string;
  /** #980: id de cliente, `none` (sin cliente) o vacío (todos). */
  clientId?: string;
  /** `true`, `false` o `pending` (sin decidir). */
  reimbursable?: string;
  /** `fijo` o `variable` (tipo de costo efectivo); vacío = todos. */
  costType?: string;
  /** `true` = solo los gastos marcados como pérdida (#1177). */
  loss?: string;
  accountId?: string;
  includePayroll?: boolean;
}

const E = EXPENSES_ENDPOINTS;

export const fetchAccess = async () => data<AccessInfo>(await api.get(E.access));
export const saveAccessEmails = async (emails: string[]) => data<AccessInfo>(await api.put(E.accessList, { emails }));

export const savePayrollRates = async (exchangeRates: Record<string, number | null>) =>
  data<Record<string, PayrollRate>>(await api.put(E.payrollRates, { exchangeRates }));
export const fetchExpenses = async (params: ListParams) =>
  data<{ expenses: ExpenseRow[]; payrollSkipped: number }>(await api.get(E.list, { params }));
export const createExpense = async (payload: ExpenseInput) => data<Expense>(await api.post(E.item, payload));
export const updateExpense = async (id: string, payload: ExpenseInput) => data<Expense>(await api.put(E.item, payload, { params: { id } }));
export const deleteExpense = async (id: string) => data(await api.delete(E.item, { params: { id } }));
/** Un gasto por su id (lo abre el enlace «Ver gasto» de Activos, #1638). */
export const fetchExpense = async (id: string) => data<Expense>(await api.get(E.item, { params: { id } }));

export async function uploadAttachment(id: string, file: File, kind: "comprobante" | "factura") {
  const form = new FormData();
  form.append("file", file);
  form.append("kind", kind);
  return data<{ expense: Expense }>(await api.post(E.attachment, form, { ...MULTIPART, params: { id } }));
}
export const removeAttachment = async (id: string, path: string) => data<Expense>(await api.delete(E.attachment, { params: { id, path } }));
export const attachmentUrl = async (path: string) => data<{ url: string }>(await api.get(E.attachmentUrl, { params: { path } }));

export async function extractFile(file: File, hint?: string, opts: { statement?: boolean; accountId?: string; kind?: DocumentKindChoice } = {}) {
  const form = new FormData();
  form.append("file", file);
  if (hint) form.append("hint", hint);
  // Tipo de documento elegido por quien sube (#1274): estado de cuenta o una sola operación.
  if (opts.kind && opts.kind !== "auto") form.append("kind", opts.kind);
  // Estado de cuenta marcado por quien sube (#1029): se guarda cifrado aunque la IA no lo reconozca.
  if (opts.statement) form.append("statement", "true");
  // Cuenta o tarjeta del estado de cuenta (#952).
  if (opts.accountId) form.append("accountId", opts.accountId);
  return data<ImportRecord>(await api.post(E.extract, form, { ...MULTIPART, timeout: 300_000 }));
}
export const confirmImport = async (
  id: string,
  charges: (PreviewCharge & { forceDuplicate?: boolean })[],
  defaults: { justification?: string; paymentMethod?: string; cardLast4?: string; exchangeRates?: Record<string, number> },
  // Estados de cuenta (#952 / #954): cuenta, periodo, total declarado y tipo de cada renglón.
  extra: { accountId?: string | null; statementPeriod?: { from: string; to: string } | null; statementTotal?: number | null; kinds?: Record<number, string> } = {},
) => data<{ created: number; skipped: { index: number; reason: string }[] }>(await api.post(E.importConfirm, { charges, defaults, ...extra }, { params: { id } }));
export const discardImport = async (id: string) => data(await api.delete(E.importItem, { params: { id } }));
export const fetchImports = async () => data<ImportRecord[]>(await api.get(E.importList));
export const fetchImport = async (id: string) => data<ImportRecord>(await api.get(E.importItem, { params: { id } }));

export const fetchSummary = async (params: { from?: string; to?: string; clientId?: string }) =>
  data<SummaryResponse>(await api.get(E.summary, { params }));

// Pérdidas (#1177): nómina sin horas justificadas + gastos marcados como pérdida.
export interface LossesResponse extends LossSummary {
  from: string;
  to: string;
  /** `null` si no se pudo cruzar con Horas Trabajadas (ver `hoursError`). */
  payrollHours: {
    /** Meses cuyas horas no se pudieron leer completas: quedan fuera del cruce. */
    uncovered: string[];
    /** Meses anteriores al primer registro de horas: no había con qué justificar, no son pérdida. */
    beforeTracking?: string[];
    /** Primer mes que sí se cruza (`null` si aún no hay registros de horas). */
    trackingFrom?: string | null;
    /** Los montos que permiten despejar un sueldo solo viajan a los dueños. */
    months: (Pick<PayrollHoursMonth, "period" | "inProgress" | "paidHours" | "justifiedHours" | "unjustifiedHours" | "untrackedHours" | "lossBase" | "withoutRate"> & Partial<PayrollHoursMonth>)[];
    totals: Pick<PayrollHoursResult["totals"], "paidHours" | "justifiedHours" | "unjustifiedHours" | "untrackedHours" | "lossBase" | "withoutRate"> & Partial<PayrollHoursResult["totals"]>;
    /** Lleva sueldos: solo viaja a los dueños. */
    byPerson: PayrollHoursPerson[] | null;
  } | null;
  hoursError: string | null;
  expenses: { id: string; date: string; vendor: string; category: string; amountBase: number | null; lossReason: string; lossNote: string | null }[];
}
export const fetchLosses = async (params: { from?: string; to?: string }) => data<LossesResponse>(await api.get(E.losses, { params }));

// Flujo de efectivo: ingresos contra egresos.
export type CashflowResponse = CashflowSummary & { from: string; to: string; incomeCount: number };
export const fetchCashflow = async (params: { from?: string; to?: string }) => data<CashflowResponse>(await api.get(E.cashflow, { params }));

// Ingreso esperado desde el CRM (#1290): contratos fijos, facturas por cobrar y
// pipeline ponderado, por mes. Lo comparten Presupuestos, Ingresos y el
// pronóstico del CRM.
export interface ExpectedIncomeMonth {
  period: string;
  phase: "past" | "current" | "future";
  /** Lo que ya entró (ingresos reales de la empresa). */
  actual: number;
  /** Cortes que faltan de los contratos corriendo. */
  contracts: number;
  /** Saldo de facturas emitidas que vencen en el mes (lo vencido, en el mes en curso). */
  receivable: number;
  /** Pipeline abierto ponderado por probabilidad. */
  pipeline: number;
  /** El mismo pipeline sin ponderar. */
  pipelineFull: number;
  /** Cotizaciones enviadas sin vencer, ponderadas por la etapa de su negocio (#1546). */
  quotes?: number;
  /** Las mismas cotizaciones sin ponderar. */
  quotesFull?: number;
  /** contracts + receivable + pipeline + quotes (0 en meses pasados). */
  expected: number;
}
export interface ExpectedContract {
  dealId: string;
  title: string;
  client: string;
  frequency: "monthly" | "biweekly";
  days: number[];
  /** Monto por periodo, en su moneda. */
  amount: number;
  currency: string;
  amountBase: number | null;
  /** Lo que deja al mes en pesos (`null` sin tipo de cambio). */
  monthly: number | null;
  since: string | null;
  nextCut: { date: string; period: string; dueDate: string } | null;
}
export interface ExpectedReceivable {
  id: string;
  number: string | null;
  dealId: string | null;
  client: string;
  dueDate: string;
  balance: number;
  currency: string;
  balanceBase: number | null;
  /** Parte del saldo que ya entró con un pago sin aplicar: no se espera otra vez. */
  covered?: number;
  overdue: boolean;
  period: string;
  /** Origen de la factura (`cotizacion` si nació de una cotización aceptada, #1546). */
  source?: string | null;
  quoteId?: string | null;
  quoteFolio?: string | null;
}
export interface ExpectedPipelineDeal {
  dealId: string;
  title: string;
  client: string;
  stage: string;
  probability: number;
  /** Monto del negocio en su moneda (`currency`); `amountBase` en pesos (`null` sin tipo de cambio). */
  amount: number;
  currency?: string;
  amountBase?: number | null;
  weighted: number;
  closeDate: string | null;
  period: string | null;
  recurring: boolean;
  perPeriod: number | null;
  monthly: number | null;
}
/** Una cotización enviada sin vencer dentro del ingreso esperado (#1546). */
export interface ExpectedQuote {
  quoteId: string;
  folio: string | null;
  title: string;
  dealId: string | null;
  client: string;
  /** Etapa del negocio (`null` sin negocio). */
  stage: string | null;
  status: string;
  /** Probabilidad con que se pondera: la de la etapa del negocio, o la de «propuesta». */
  probability: number;
  total: number;
  currency: string;
  totalBase: number | null;
  weighted: number | null;
  expiresAt: string | null;
  /** Cierre del negocio o, si no tiene, la vigencia de la cotización. */
  expectedDate: string | null;
  period: string | null;
}
/** Un renglón del calendario de pagos de Cobranza → Pagos (#1324). */
export interface ExpectedPayment {
  key: string;
  /** `corte`: corte de un contrato fijo aún sin factura; `factura`: factura emitida. */
  kind: "corte" | "factura";
  status: "por_facturar" | "por_cobrar" | "vencida" | "cobrada";
  dealId: string | null;
  client: string;
  title: string;
  /** Fecha de corte o de emisión. */
  date: string;
  dueDate: string;
  paidAt: string | null;
  /** Mes en que se espera (o en que se cobró). */
  period: string;
  /** Monto en su moneda: el saldo de una factura abierta, lo pagado de una cobrada. */
  amount: number;
  currency: string;
  amountBase: number | null;
  /** Lo que falta por entrar, en pesos (0 si ya se cobró; `null` sin tipo de cambio). */
  expectedBase: number | null;
  invoiceId: string | null;
  number: string | null;
  /** Origen de la factura; `cotizacion` trae la cotización de la que nació (#1546). */
  source?: string | null;
  quoteId?: string | null;
  quoteFolio?: string | null;
}
/** Negocio ganado sin cobro recurrente capturado: su pago no se puede proyectar. */
export interface UnscheduledDeal {
  dealId: string;
  title: string;
  client: string;
  amount: number;
  currency: string;
}
export interface ExpectedIncomeResponse {
  from: string;
  to: string;
  today: string;
  months: ExpectedIncomeMonth[];
  totals: Pick<ExpectedIncomeMonth, "actual" | "contracts" | "receivable" | "pipeline" | "pipelineFull" | "quotes" | "quotesFull" | "expected">;
  /** Lo que entra fijo cada mes por los contratos corriendo. */
  monthlyFixed: number;
  contracts: ExpectedContract[];
  receivables: ExpectedReceivable[];
  pipeline: ExpectedPipelineDeal[];
  /** Cotizaciones enviadas sin vencer (una por negocio), ponderadas (#1546). */
  quotes?: ExpectedQuote[];
  quotesUndated?: { count: number; weighted: number; full: number };
  quotesBeyond?: { count: number; weighted: number; full: number };
  /** Calendario pago por pago; lo abierto suma `contracts` + `receivable` de cada mes. */
  payments?: ExpectedPayment[];
  unscheduled?: UnscheduledDeal[];
  /** Negocios abiertos sin fecha de cierre: no entran al flujo por mes. */
  undated: { count: number; weighted: number; full: number };
  /** Negocios que cierran después del rango pedido. */
  beyond: { count: number; weighted: number; full: number };
  /** Monedas sin tipo de cambio: lo suyo no suma en pesos. */
  withoutRate: string[];
}
// Nómina del periodo persona por persona (#1424): de dónde sale lo «gastado» en
// nómina. Lleva sueldos: el API solo se lo entrega a los dueños (403 al resto).
export interface PayrollBreakdownPerson {
  memberId: string;
  name: string;
  currency: string;
  /** Sueldo del mes: lo de un periodo × los periodos del mes. */
  salary: number;
  bonus: number;
  amount: number;
  /** En pesos (`null` sin tipo de cambio). */
  amountBase: number | null;
}
export interface PayrollBreakdownExpense {
  id: string;
  date: string;
  vendor: string;
  description: string;
  amount: number;
  currency: string;
  amountBase: number | null;
  /** `nomina`: recibo pagado · `equipo`: liquidación · lo demás: gasto capturado o de un estado de cuenta. */
  source: string;
}
export interface PayrollBreakdownMonth {
  period: string;
  phase: "past" | "current" | "future";
  /** Estimado del padrón: quién y con cuánto. */
  estimated: PayrollBreakdownPerson[];
  /** Pagos de nómina ya registrados ese mes. */
  registered: PayrollBreakdownExpense[];
  estimatedTotal: number;
  registeredTotal: number;
  total: number;
}
export interface PayrollBreakdownResponse {
  from: string;
  to: string;
  today: string;
  months: PayrollBreakdownMonth[];
  totals: { estimated: number; registered: number; total: number };
  /** A quién no se estimó y por qué. */
  skipped: { memberId: string; name: string; reason: "sin_sueldo" | "pagado"; period: string | null }[];
  withoutRate: string[];
}
export const fetchPayrollBreakdown = async (params: { from: string; to: string }) => data<PayrollBreakdownResponse>(await api.get(E.payrollBreakdown, { params }));

export const fetchExpectedIncome = async (params: { from?: string; to?: string } = {}) => data<ExpectedIncomeResponse>(await api.get(E.expectedIncome, { params }));
// Estados financieros: resultados y flujo de efectivo por mes + balance al corte.
export interface FinancialsResponse {
  from: string;
  to: string;
  /** Primer mes con movimientos: desde ahí acumula el balance. */
  inception: string;
  /** Fecha de corte del balance (fin de `to`, sin pasar de hoy). */
  asOf: string;
  months: FinancialMonth[];
  /** Efectivo según libros al inicio del rango. */
  openingCash: number;
  balance: BalanceSheet;
  /** Partidas del balance capturadas a mano. */
  items: BalanceItem[];
  withoutRate: number;
  depreciationMonths: number;
}
export const fetchFinancials = async (params: { from?: string; to?: string }) => data<FinancialsResponse>(await api.get(E.financials, { params }));
export const saveBalanceItems = async (items: Omit<BalanceItem, "id">[] | BalanceItem[]) => data<{ items: BalanceItem[] }>(await api.put(E.balanceItems, { items }));
// Dispersión de nómina (NVIO): transferencias contra el padrón.
export interface PayoutResponse {
  /** Elección guardada; `memberIds` vacío = automática (quienes cobran en otra moneda). */
  config: PayoutConfig | null;
  /** A quiénes cubre hoy la dispersión (la elección o la automática). */
  coveredIds?: string[];
  candidates: { id: string; name: string; currency: string; hasSalary: boolean }[];
  reconciliation: PayoutReconciliation | null;
}
export const fetchPayout = async () => data<PayoutResponse>(await api.get(E.payout));
export const savePayout = async (payload: { account: string; label: string; memberIds: string[]; tolerance?: number | null }) =>
  data<PayoutResponse>(await api.put(E.payout, payload));
export const fetchIncomes = async (params: { from?: string; to?: string }) => data<Income[]>(await api.get(E.incomeList, { params }));
export const saveIncome = async (payload: IncomeInput, id?: string) =>
  data<Income>(id ? await api.put(E.income, payload, { params: { id } }) : await api.post(E.income, payload));
export const deleteIncome = async (id: string) => data(await api.delete(E.income, { params: { id } }));
// Ingresos leídos con IA (#1274): la IA propone; nada se guarda hasta revisar.
export type IncomeExtractionResponse = Omit<IncomeExtraction, "incomes"> & { incomes: IncomePreview[]; matches?: IncomeMatch[]; declared: DocumentKindChoice; fileName: string };
export async function extractIncomeFile(file: File, kind: DocumentKindChoice) {
  const form = new FormData();
  form.append("file", file);
  form.append("kind", kind);
  return data<IncomeExtractionResponse>(await api.post(E.incomeExtract, form, { ...MULTIPART, timeout: 300_000 }));
}

export const fetchBudgets = async () => data<BudgetLine[]>(await api.get(E.budgetList));
export const saveBudget = async (payload: Omit<BudgetLine, "id">, id?: string) =>
  data<BudgetLine>(id ? await api.put(E.budget, payload, { params: { id } }) : await api.post(E.budget, payload));
export const deleteBudget = async (id: string) => data(await api.delete(E.budget, { params: { id } }));

// Ajuste semanal de presupuestos (#1178): vista previa + última corrida / ajustar ahora.
export interface BudgetAdjustmentRun extends Pick<BudgetAdjustmentPlan, "from" | "changes" | "skipped" | "totalBefore" | "totalAfter"> {
  runAt: string;
  trigger: "semanal" | "manual";
  userId: string | null;
}
export interface BudgetAdjustmentResponse extends BudgetAdjustmentPlan {
  today: string;
  rates: Partial<Record<string, CategoryRunRate>>;
  last: BudgetAdjustmentRun | null;
}
// --- Configuración de Finanzas (#1255): categorías y tipo de cambio -------------------
export type CategoryUsage = { expenses: number; budgets: number; platforms: number };
export type CategoryRow = CategoryDef & { usage?: CategoryUsage };
export const fetchCategories = async (usage = false) =>
  data<{ categories: CategoryRow[]; canEdit: boolean }>(await api.get(E.financeCategoryList, usage ? { params: { usage: true } } : undefined));
export const saveCategory = async (payload: CategoryInput, id?: string) =>
  data<CategoryDef>(id ? await api.put(E.financeCategory, payload, { params: { id } }) : await api.post(E.financeCategory, payload));
/** «Borrar»: elimina la propia sin movimientos; si los tiene (o es del sistema), la archiva. */
export const deleteCategory = async (id: string) => data<{ id: string; result: "deleted" | "archived" }>(await api.delete(E.financeCategory, { params: { id } }));

export interface FxCurrencyState {
  currency: string;
  /** El oficial de hoy (Banxico / BCE); null si la API no respondió. */
  official: { rate: number; date: string | null; source: string | null } | null;
  /** El fijado vigente hoy, si hay. */
  pinned: { rate: number; from: string; by: string | null } | null;
  /** El que se usa hoy: el fijado o, sin él, el oficial. */
  effective: { rate: number; source: string | null } | null;
  /** Fijaciones, la más reciente primero (`rate: null` = volvió al oficial). */
  history: { from: string; rate: number | null; by: string | null; at: string | null; upcoming: boolean }[];
}
export interface FxSettingsResponse {
  base: string;
  today: string;
  currencies: FxCurrencyState[];
  canEdit: boolean;
}
/** Plazo de pago de las facturas a clientes (Finanzas → Configuración). */
export interface InvoiceTermsResponse {
  /** Días después del corte en que vence una factura. */
  dueDays: number;
  defaultDueDays: number;
  maxDueDays: number;
  updatedBy: string | null;
  updatedAt: string | null;
  canEdit: boolean;
}
const INVOICE_TERMS = "/expensesInvoiceTerms";
export const fetchInvoiceTerms = async () => data<InvoiceTermsResponse>(await api.get(INVOICE_TERMS));
export const saveInvoiceTerms = async (dueDays: number) => data<InvoiceTermsResponse>(await api.put(INVOICE_TERMS, { dueDays }));

export const fetchFx = async () => data<FxSettingsResponse>(await api.get(E.financeFx));
/** `rate: null` libera la moneda (vuelve al oficial) desde `from` (hoy si no se da). */
export const saveFxPin = async (payload: { currency: string; rate: number | null; from?: string }) => data<FxSettingsResponse>(await api.put(E.financeFx, payload));
/** Tipo de cambio que aplica a una fecha: se propone al capturar un gasto en otra moneda. */
export const fetchFxRate = async (currency: string, date?: string) =>
  data<{ currency: string; date: string; rate: number | null; source: string | null }>(await api.get(E.financeFxRate, { params: { currency, ...(date ? { date } : {}) } }));

export const fetchBudgetAdjustment = async () => data<BudgetAdjustmentResponse>(await api.get(E.budgetAdjust));
export const applyBudgetAdjustment = async () => data<BudgetAdjustmentPlan & { runAt: string; applied: number }>(await api.post(E.budgetAdjust));

export const fetchPlatforms = async () => data<Platform[]>(await api.get(E.platformList));
export const savePlatform = async (payload: Partial<Platform>, id?: string) =>
  data<Platform & { linkedExpenses?: number; recategorizedExpenses?: number }>(id ? await api.put(E.platform, payload, { params: { id } }) : await api.post(E.platform, payload));
export const deletePlatform = async (id: string) => data(await api.delete(E.platform, { params: { id } }));
export const fetchPlatformReport = async (params: { from?: string; to?: string }) =>
  data<{ from: string; to: string; platforms: PlatformReport[] }>(await api.get(E.platformReport, { params }));

export const fetchInboxStatus = async () => data<InboxStatus>(await api.get(E.inboxStatus));
export const fetchInbox = async () => data<InboxLogRow[]>(await api.get(E.inboxList));
export const runInbox = async () => data<{ configured: boolean; processed: number; failed: number }>(await api.post(E.inboxRun, {}, { timeout: 300_000 }));
export async function uploadToInbox(files: File[]) {
  const form = new FormData();
  files.forEach((f, i) => form.append(`file${i}`, f));
  return data<InboxLogRow>(await api.post(E.inboxUpload, form, { ...MULTIPART, timeout: 300_000 }));
}
